import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const MOBILE_SELECTION_STEPS = [
	'ethnicity',
	'age',
	'hairstyle',
	'haircolor',
	'bodytype',
	'eyecolor',
	'intimatesize',
];

const MOBILE_SCROLL_STEPS = [...MOBILE_SELECTION_STEPS, 'final', 'result'];

const NO_SCROLL_STEPS = ['gender', 'generating'];

const mobileScrollSpace = css`
	padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
	scroll-padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
`;

const noScrollStageContentRules = css`
	${NO_SCROLL_STEPS.map(
		(step) => css`
			[data-studio-step='${step}'] & {
				flex: 1 1 auto;
				min-height: 0;
				overflow: hidden;
				display: flex;
				flex-direction: column;
			}
		`,
	)}
`;

const mobileSelectionStageContentRules = css`
	${MOBILE_SCROLL_STEPS.map(
		(step) => css`
			[data-studio-step='${step}'] & {
				@media (max-width: ${THEME.mobileBreakpoint}) {
					flex: 0 0 auto;
					min-height: min-content;
					overflow: visible;
				}
			}
		`,
	)}
`;

const studioCardSelected = css`
	border-color: rgba(255, 77, 242, 0.62);
	opacity: 1 !important;
	filter: none !important;
	z-index: 3;
	box-shadow:
		0 0 0 1px rgba(255, 77, 242, 0.22) inset,
		0 14px 40px rgba(0, 0, 0, 0.42),
		0 0 28px rgba(255, 77, 242, 0.16);

	span {
		border-color: rgba(255, 162, 241, 0.42);
		background: rgba(24, 9, 34, 0.84);
		box-shadow:
			0 8px 20px rgba(0, 0, 0, 0.28),
			inset 0 1px 0 rgba(255, 255, 255, 0.08);
	}

	&:hover,
	&:focus-visible {
		border-color: rgba(255, 77, 242, 0.72);
		box-shadow:
			0 0 0 1px rgba(255, 77, 242, 0.28) inset,
			0 16px 44px rgba(0, 0, 0, 0.44),
			0 0 32px rgba(255, 77, 242, 0.2);
	}
`;

const studioCardDimmed = css`
	opacity: 0.68;
	filter: saturate(0.86) brightness(0.9);
`;

const studioCardSelection = css`
	&[aria-pressed='true'],
	&[data-selected='true'] {
		${studioCardSelected}
	}
`;

const studioCardDimmedProp = css`
	${(props) => props.$dimmed && studioCardDimmed}
`;

export const StudioView = styled.section`
	flex: 1 1 auto;
	min-height: 0;
	height: 100%;
	display: flex;
	flex-direction: column;
`;

export const StudioScreen = styled.div`
	flex: 1 1 auto;
	height: 100%;
	max-height: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: clamp(6px, 1.4vh, 12px);
	padding: clamp(14px, 2.2vw, 24px) clamp(18px, 4vw, 48px)
		clamp(16px, 2.5vh, 22px);
	overflow-x: hidden;
	overflow-y: auto;
	overscroll-behavior: contain;
	-webkit-overflow-scrolling: touch;

	@media (max-width: 1080px) {
		padding-left: 26px;
		padding-right: 26px;
	}

	@media (max-height: 860px) and (min-width: ${THEME.mobileBreakpoint}) {
		gap: 6px;
		padding-top: 12px;
		padding-bottom: 14px;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 8px ${THEME.mobileGutter} max(8px, env(safe-area-inset-bottom, 0px));
	}

	${MOBILE_SCROLL_STEPS.map(
		(step) => css`
			&[data-studio-step='${step}'] {
				@media (max-width: ${THEME.mobileBreakpoint}) {
					${mobileScrollSpace}
				}
			}
		`,
	)}

	${NO_SCROLL_STEPS.map(
		(step) => css`
			&[data-studio-step='${step}'] {
				overflow-y: hidden;
				overscroll-behavior: none;

				@media (max-width: ${THEME.mobileBreakpoint}) {
					padding-bottom: max(8px, env(safe-area-inset-bottom, 0px));
				}
			}
		`,
	)}

	&[data-studio-step='generating'] {
		@media (max-width: ${THEME.mobileBreakpoint}) {
			gap: 0;
			padding: ${THEME.mobileGutter};
			background:
				radial-gradient(
					circle at 18% 12%,
					rgba(176, 38, 255, 0.14),
					transparent 38%
				),
				radial-gradient(
					circle at 88% 88%,
					rgba(255, 77, 242, 0.08),
					transparent 34%
				);
		}

		@media (min-width: calc(${THEME.mobileBreakpoint} + 1px)) {
			background:
				radial-gradient(
					circle at 18% 12%,
					rgba(176, 38, 255, 0.14),
					transparent 38%
				),
				radial-gradient(
					circle at 88% 88%,
					rgba(255, 77, 242, 0.08),
					transparent 34%
				);
		}
	}

	&[data-studio-step='gender'] {
		@media (max-width: ${THEME.mobileBreakpoint}) {
			gap: 6px;
			padding: 6px ${THEME.mobileGutter}
				max(6px, env(safe-area-inset-bottom, 0px));
			background:
				radial-gradient(
					circle at 50% 0%,
					rgba(176, 38, 255, 0.16),
					transparent 42%
				),
				radial-gradient(
					circle at 100% 100%,
					rgba(255, 77, 242, 0.08),
					transparent 36%
				);
		}
	}
`;

export const StudioStageContent = styled.div`
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	${mobileSelectionStageContentRules}
	${noScrollStageContentRules}
`;

export const StudioHeader = styled.header`
	flex-shrink: 0;
	display: grid;
	align-items: center;
	gap: 8px 12px;
	width: 100%;
	max-width: min(100%, 980px);
	margin: 0 auto;

	[data-studio-step='gender'] & {
		@media (max-width: ${THEME.mobileBreakpoint}) {
			position: relative;
			z-index: 5;
			margin-bottom: 0;
			padding-top: 0;
		}
	}

	${(props) =>
		props.$hasBack
			? css`
					grid-template-columns: 34px minmax(0, 1fr) 34px;

					@media (max-width: ${THEME.mobileBreakpoint}) {
						grid-template-columns: 30px minmax(0, 1fr) 30px;
					}
				`
			: css`
					grid-template-columns: minmax(0, 1fr);
				`}
`;

export const StudioHeaderBack = styled.div`
	grid-column: 1;
	grid-row: 1;
	align-self: start;
	justify-self: start;
	padding-top: 0.2em;
`;

export const StudioHeaderCopy = styled.div`
	grid-column: ${(props) => (props.$hasBack ? '2' : '1')};
	text-align: center;
	display: grid;
	gap: 4px;
	min-width: 0;

	h1 {
		margin: 0;
		font-family: ${THEME.fontDisplay};
		font-size: clamp(2.15rem, 3.31vw, 3.25rem);
		line-height: 1.04;
		font-weight: 700;
		color: #ffffff;
		text-shadow: 0 0 18px rgba(255, 77, 242, 0.15);

		span {
			color: #fff;
			background: ${THEME.gradientHighlight};
			-webkit-background-clip: text;
			background-clip: text;
			-webkit-text-fill-color: transparent;
			filter: drop-shadow(0 0 12px rgba(255, 77, 242, 0.42));
		}
	}

	p {
		margin: 0;
		color: rgba(255, 225, 250, 0.78);
		font-size: clamp(0.82rem, 1.15vw, 0.98rem);
		font-weight: 800;
	}

	@media (max-height: 860px) and (min-width: ${THEME.mobileBreakpoint}) {
		h1 {
			font-size: clamp(1.98rem, 2.94vw, 2.75rem);
		}

		p {
			font-size: clamp(0.78rem, 1vw, 0.9rem);
		}
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 8px;
		padding-bottom: 0;

		h1 {
			font-size: clamp(1.28rem, 5.8vw, 1.58rem);
			line-height: 1.1;
		}

		p {
			font-size: 0.78rem;
			line-height: 1.35;
			color: rgba(255, 225, 250, 0.82);
			font-weight: 700;
		}
	}

	[data-studio-step='gender'] & {
		@media (max-width: ${THEME.mobileBreakpoint}) {
			gap: 6px;
			padding-bottom: 4px;
		}
	}
`;

export const StudioProgressRail = styled.div`
	flex-shrink: 0;
	width: 100%;
	max-width: min(100%, 560px);
	margin: 0 auto;
	padding: 0 4px;
`;

export const GenderStageSection = styled.section`
	position: relative;
	width: min(100%, 900px);
	flex: 0 0 auto;
	align-self: center;
	margin: 0 auto;
	display: grid;
	place-items: center;
	align-content: start;
	padding: clamp(32px, 5.5vh, 56px) 0 clamp(14px, 2.5vh, 28px);
	min-height: clamp(380px, 48vh, 470px);
	isolation: isolate;

	@media (max-height: 860px) and (min-width: ${THEME.mobileBreakpoint}) {
		min-height: clamp(340px, 44vh, 410px);
		padding: clamp(22px, 4vh, 36px) 0 18px;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		flex: 1 1 auto;
		min-height: 0;
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		grid-template-rows: minmax(0, 1.35fr) minmax(0, 1fr);
		align-content: stretch;
		justify-items: stretch;
		gap: 8px;
		padding: 2px 0 8px;
		overflow: hidden;

		&::before {
			content: '';
			position: absolute;
			inset: 4% -8% 8%;
			background:
				radial-gradient(
					circle at 50% 18%,
					rgba(255, 77, 242, 0.2),
					transparent 52%
				),
				radial-gradient(
					circle at 18% 78%,
					rgba(176, 38, 255, 0.12),
					transparent 42%
				),
				radial-gradient(
					circle at 82% 82%,
					rgba(255, 178, 108, 0.1),
					transparent 38%
				);
			pointer-events: none;
			z-index: 0;
		}
	}

	@media (max-width: ${THEME.mobileBreakpoint}) and (max-height: 740px) {
		gap: 8px;
	}
`;

export const StudioCardImageSlot = styled.div`
	position: absolute;
	inset: 0;
`;

export const GenderCardImage = styled.div`
	position: relative;
	width: 100%;
	aspect-ratio: 0.747;
	overflow: hidden;
	border-radius: inherit;
`;

export const GenderCardMedia = styled.div`
	position: relative;
	width: 100%;
	height: 100%;

	picture,
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center top;
		filter: none;
		transition:
			opacity 180ms ease,
			transform 180ms ease,
			filter 180ms ease;
	}

	&[data-hovered='true'] picture,
	&[data-hovered='true'] img {
		opacity: 0;
	}
`;

export const GenderCardVideo = styled.video`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center top;
	opacity: ${({ $visible }) => ($visible ? 1 : 0)};
	transition: opacity 180ms ease, transform 180ms ease;
	z-index: 1;
	pointer-events: none;
`;

const genderCardReveal = keyframes`
	from {
		opacity: 0;
		transform: translateY(14px) scale(0.98);
	}

	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
`;

const genderCardMobile = css`
	@media (max-width: ${THEME.mobileBreakpoint}) {
		position: relative;
		top: auto;
		left: auto;
		z-index: 1;
		display: block;
		width: 100%;
		max-width: none;
		min-height: 0;
		padding: 0;
		border-radius: 22px;
		border: 1px solid rgba(255, 255, 255, 0.12);
		opacity: 1;
		filter: none;
		transform: none;
		overflow: hidden;
		isolation: isolate;
		animation: ${genderCardReveal} 520ms cubic-bezier(0.22, 1, 0.36, 1) both;
		box-shadow:
			0 22px 54px rgba(0, 0, 0, 0.42),
			inset 0 1px 0 rgba(255, 255, 255, 0.08);

		${GenderCardImage} {
			position: absolute;
			inset: 0;
			width: 100%;
			height: 100%;
			aspect-ratio: unset;
			border-radius: inherit;

			picture,
			img {
				object-fit: cover;
				object-position: center top;
			}
		}

		span {
			position: absolute;
			left: 14px;
			right: 14px;
			bottom: 14px;
			z-index: 3;
			transform: none;
			margin: 0;
			padding: 9px 14px;
			border: 1px solid rgba(255, 180, 236, 0.28);
			border-radius: 999px;
			background: rgba(12, 5, 18, 0.72);
			backdrop-filter: blur(14px) saturate(1.2);
			box-shadow:
				0 10px 24px rgba(0, 0, 0, 0.28),
				inset 0 1px 0 rgba(255, 255, 255, 0.08);
			color: #fff;
			font-size: 0.9rem;
			font-weight: 800;
			line-height: 1.1;
			text-align: center;
			text-shadow: 0 0 16px rgba(255, 119, 244, 0.2);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		&::before {
			content: '';
			position: absolute;
			inset: auto 0 0;
			height: 62%;
			z-index: 2;
			background: linear-gradient(
				180deg,
				rgba(8, 3, 12, 0) 0%,
				rgba(8, 3, 12, 0.55) 52%,
				rgba(8, 3, 12, 0.92) 100%
			);
			pointer-events: none;
		}

		&::after {
			content: '';
			position: absolute;
			inset: 0;
			z-index: 2;
			background: linear-gradient(
				145deg,
				rgba(255, 255, 255, 0.1) 0%,
				transparent 34%,
				transparent 66%,
				rgba(255, 77, 242, 0.08) 100%
			);
			pointer-events: none;
			opacity: 0.75;
		}

		&:active ${GenderCardImage} img {
			transform: scale(1.05);
			transform-origin: center top;
		}

		&:hover,
		&:focus-visible {
			transform: translateY(-3px);
			filter: none;
			border-color: rgba(255, 255, 255, 0.24);
			box-shadow:
				0 26px 60px rgba(0, 0, 0, 0.48),
				0 0 28px rgba(255, 77, 242, 0.12);
		}
	}

	@media (max-width: ${THEME.mobileBreakpoint}) and (max-height: 740px) {
		border-radius: 18px;

		span {
			left: 10px;
			right: 10px;
			bottom: 10px;
			padding: 7px 12px;
			font-size: 0.82rem;
		}
	}
`;

const genderCardBase = css`
	position: relative;
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 8px;
	padding: 0;
	overflow: visible;
	background: #100717;
	box-shadow: 0 24px 80px rgba(0, 0, 0, 0.42);
	cursor: pointer;
	transition:
		transform 180ms ease,
		border-color 180ms ease,
		box-shadow 180ms ease,
		filter 180ms ease,
		opacity 180ms ease;

	${GenderCardImage} {
		width: 100%;
		min-height: 0;
	}

	img {
		object-fit: cover;
		border-radius: inherit;
	}

	span {
		position: absolute;
		left: 50%;
		bottom: -34px;
		transform: translateX(-50%);
		z-index: 2;
		color: #ffffff;
		border: 1px solid rgba(255, 180, 236, 0.2);
		border-radius: 999px;
		padding: 6px 12px 5px;
		background: rgba(16, 7, 23, 0.78);
		box-shadow:
			0 12px 28px rgba(0, 0, 0, 0.34),
			inset 0 1px 0 rgba(255, 255, 255, 0.06);
		font-size: 0.72rem;
		font-weight: 900;
		line-height: 1;
		text-shadow: 0 0 12px rgba(255, 119, 244, 0.16);
		white-space: nowrap;
	}

	&::after {
		content: '';
		position: absolute;
		inset: auto 0 0;
		height: 18%;
		background: linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(8, 3, 12, 0.28));
		border-radius: 0 0 8px 8px;
		pointer-events: none;
	}

	&:hover {
		border-color: rgba(255, 255, 255, 0.34);
		box-shadow:
			0 26px 86px rgba(0, 0, 0, 0.5),
			0 0 24px rgba(255, 119, 244, 0.1);
		filter: saturate(1.02) brightness(0.96);
		opacity: 1;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		overflow: hidden;
	}

	${studioCardSelection}
	${studioCardDimmedProp}
`;

const genderCardDesktop = css`
	@media (min-width: calc(${THEME.mobileBreakpoint} + 1px)) {
		position: absolute;
		top: 58%;
		left: 50%;
	}
`;

export const GenderCardMain = styled.button`
	${genderCardBase}
	${genderCardMobile}
	${genderCardDesktop}
	z-index: 4;

	@media (min-width: calc(${THEME.mobileBreakpoint} + 1px)) {
		width: min(40vw, 300px);
		height: auto;
		min-height: 0;
		transform: translate(-50%, calc(-50% + 2px));
		box-shadow:
			0 24px 90px rgba(0, 0, 0, 0.5),
			0 0 0 2px rgba(255, 255, 255, 0.1);

		&:hover {
			transform: translate(-50%, calc(-50% - 4px)) scale(1.012);
			box-shadow:
				0 28px 94px rgba(0, 0, 0, 0.54),
				0 0 0 2px rgba(255, 255, 255, 0.16),
				0 0 26px rgba(255, 119, 244, 0.12);
		}
	}

	@media (max-height: 860px) and (min-width: ${THEME.mobileBreakpoint}) {
		width: min(36vw, 268px);
	}

	@media (max-width: 1080px) and (min-width: calc(${THEME.mobileBreakpoint} + 1px)) {
		width: min(44vw, 280px);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		position: relative;
		top: auto;
		left: auto;
		grid-column: 1 / -1;
		grid-row: 1;
		width: 100%;
		height: 100%;
		min-height: 0;
		aspect-ratio: auto;
		max-height: none;
		animation-delay: 60ms;
		transform: none;
		box-shadow:
			0 28px 64px rgba(0, 0, 0, 0.48),
			inset 0 1px 0 rgba(255, 255, 255, 0.1);

		span {
			left: 14px;
			right: 14px;
			bottom: 14px;
			transform: none;
			font-size: 0.92rem;
			font-weight: 900;
			letter-spacing: 0.02em;
		}

		&:hover,
		&:focus-visible {
			transform: none;
			filter: none;
			opacity: 1;
		}
	}

	@media (max-width: ${THEME.mobileBreakpoint}) and (max-height: 740px) {
		span {
			left: 10px;
			right: 10px;
			bottom: 10px;
			padding: 7px 12px;
			font-size: 0.82rem;
		}
	}
`;

export const GenderCardTrans = styled.button`
	${genderCardBase}
	${genderCardMobile}
	${genderCardDesktop}
	z-index: 2;

	@media (min-width: calc(${THEME.mobileBreakpoint} + 1px)) {
		width: min(27vw, 220px);
		height: auto;
		min-height: 0;
		opacity: 0.82;
		filter: saturate(0.9) brightness(0.82);
		transform: translate(
				calc(-50% + clamp(-220px, -21vw, -130px)),
				calc(-50% + 20px)
			)
			rotate(-10deg);

		&:hover {
			transform: translate(
					calc(-50% + clamp(-220px, -21vw, -130px)),
					calc(-50% + 12px)
				)
				rotate(-9deg) scale(1.025);
		}
	}

	@media (max-height: 860px) and (min-width: ${THEME.mobileBreakpoint}) {
		width: min(24vw, 196px);
		transform: translate(
				calc(-50% + clamp(-190px, -19vw, -118px)),
				calc(-50% + 16px)
			)
			rotate(-10deg);

		&:hover {
			transform: translate(
					calc(-50% + clamp(-190px, -19vw, -118px)),
					calc(-50% + 10px)
				)
				rotate(-9deg) scale(1.025);
		}
	}

	@media (max-width: 1080px) and (min-width: calc(${THEME.mobileBreakpoint} + 1px)) {
		width: min(30vw, 200px);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		position: relative;
		top: auto;
		left: auto;
		grid-column: 1;
		grid-row: 2;
		width: 100%;
		height: 100%;
		min-height: 0;
		aspect-ratio: auto;
		max-height: none;
		animation-delay: 140ms;
		transform: none;

		&:hover,
		&:focus-visible {
			transform: none;
			filter: none;
			opacity: 1;
		}
	}
`;

export const GenderCardMen = styled.button`
	${genderCardBase}
	${genderCardMobile}
	${genderCardDesktop}
	z-index: 2;

	@media (min-width: calc(${THEME.mobileBreakpoint} + 1px)) {
		width: min(27vw, 220px);
		height: auto;
		min-height: 0;
		opacity: 0.82;
		filter: saturate(0.9) brightness(0.82);
		transform: translate(
				calc(-50% + clamp(130px, 21vw, 220px)),
				calc(-50% + 18px)
			)
			rotate(10deg);

		&:hover {
			transform: translate(
					calc(-50% + clamp(130px, 21vw, 220px)),
					calc(-50% + 10px)
				)
				rotate(9deg) scale(1.025);
		}
	}

	@media (max-height: 860px) and (min-width: ${THEME.mobileBreakpoint}) {
		width: min(24vw, 196px);
		transform: translate(
				calc(-50% + clamp(118px, 19vw, 190px)),
				calc(-50% + 14px)
			)
			rotate(10deg);

		&:hover {
			transform: translate(
					calc(-50% + clamp(118px, 19vw, 190px)),
					calc(-50% + 8px)
				)
				rotate(9deg) scale(1.025);
		}
	}

	@media (max-width: 1080px) and (min-width: calc(${THEME.mobileBreakpoint} + 1px)) {
		width: min(30vw, 200px);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		position: relative;
		top: auto;
		left: auto;
		grid-column: 2;
		grid-row: 2;
		width: 100%;
		height: 100%;
		min-height: 0;
		aspect-ratio: auto;
		max-height: none;
		animation-delay: 220ms;
		transform: none;

		&:hover,
		&:focus-visible {
			transform: none;
			filter: none;
			opacity: 1;
		}
	}
`;

const optionGridBase = css`
	width: 100%;
	margin: 0 auto;
	display: grid;
	align-content: start;
	padding: clamp(16px, 3vh, 28px) 0;
`;

export const OptionGridStage = styled.section`
	${optionGridBase}
	width: min(100%, 980px);
	grid-template-columns: repeat(5, minmax(118px, 1fr));
	gap: clamp(12px, 1.7vw, 18px);

	${(props) =>
		(props.$gender === 'trans' || props.$gender === 'men') &&
		css`
			width: min(100%, 640px);
			grid-template-columns: repeat(3, minmax(140px, 1fr));
		`}

	${(props) =>
		(props.$stage === 'breastsize' || props.$stage === 'intimatesize') &&
		props.$gender === 'woman' &&
		css`
			width: min(100%, 720px);
			grid-template-columns: repeat(4, minmax(118px, 1fr));
		`}

	@media (max-width: 1080px) {
		grid-template-columns: repeat(4, minmax(112px, 1fr));
		width: min(100%, 760px);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 10px;
		padding: 8px 0 12px;
	}

	${(props) =>
		(props.$gender === 'trans' || props.$gender === 'men') &&
		css`
			@media (max-width: ${THEME.mobileBreakpoint}) {
				grid-template-columns: repeat(3, minmax(0, 1fr));
				gap: 8px;
			}
		`}
`;

export const EthnicityCard = styled.button`
	position: relative;
	min-width: 0;
	aspect-ratio: 0.72;
	border: 1px solid rgba(255, 255, 255, 0.16);
	border-radius: 8px;
	padding: 0;
	overflow: hidden;
	background: #070509;
	box-shadow: 0 18px 54px rgba(0, 0, 0, 0.34);
	cursor: pointer;
	transition:
		transform 180ms ease,
		border-color 180ms ease,
		box-shadow 180ms ease,
		filter 180ms ease;

	&::after {
		content: '';
		position: absolute;
		inset: auto 0 0;
		height: 34%;
		background: linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(8, 3, 12, 0.76));
		pointer-events: none;
	}

	span {
		position: absolute;
		left: 10px;
		right: 10px;
		bottom: 10px;
		z-index: 2;
		min-height: 30px;
		display: grid;
		place-items: center;
		border: 1px solid rgba(255, 180, 236, 0.19);
		border-radius: 999px;
		padding: 6px 9px;
		color: #ffffff;
		background: rgba(16, 7, 23, 0.76);
		box-shadow:
			0 10px 24px rgba(0, 0, 0, 0.32),
			inset 0 1px 0 rgba(255, 255, 255, 0.06);
		font-size: clamp(0.66rem, 1vw, 0.78rem);
		font-weight: 900;
		line-height: 1.1;
		text-align: center;
	}

	&:hover,
	&:focus-visible {
		transform: translateY(-4px);
		border-color: rgba(255, 255, 255, 0.3);
		box-shadow:
			0 22px 62px rgba(0, 0, 0, 0.44),
			0 0 22px rgba(255, 119, 244, 0.1);
		filter: saturate(1.03) brightness(1.02);
	}

	${(props) => props.$selected && studioCardSelected}
	${studioCardDimmedProp}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		border-radius: 12px;

		span {
			left: 8px;
			right: 8px;
			bottom: 8px;
			min-height: 26px;
			padding: 5px 8px;
			font-size: 0.68rem;
		}

		&:hover,
		&:focus-visible {
			transform: none;
		}
	}
`;

export const StudioActions = styled.nav`
	width: min(100%, 780px);
	margin: auto auto 0;
	flex-shrink: 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 14px;
	padding-top: 4px;

	${(props) =>
		props.$fullWidth &&
		css`
			width: 100%;
		`}
`;

export const StudioStepButton = styled.button`
	min-width: 118px;
	min-height: 42px;
	border: 1px solid rgba(255, 180, 236, 0.22);
	border-radius: 999px;
	padding: 0 20px;
	color: rgba(255, 231, 250, 0.92);
	background:
		linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.075),
			rgba(255, 255, 255, 0.025)
		),
		rgba(26, 15, 30, 0.86);
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.055),
		0 0 0 rgba(255, 77, 242, 0);
	font-weight: 900;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0;
	overflow: hidden;
	white-space: nowrap;
	transition:
		width 180ms ease,
		gap 180ms ease,
		padding 180ms ease,
		border-color 180ms ease,
		background 180ms ease,
		box-shadow 180ms ease,
		color 180ms ease,
		opacity 180ms ease;

	svg {
		flex: 0 0 auto;
		width: 20px;
		height: 20px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2.2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	span {
		max-width: 0;
		overflow: hidden;
		opacity: 0;
		transition:
			max-width 180ms ease,
			opacity 140ms ease;
	}

	${(props) =>
		props.$back &&
		props.$inHeader &&
		css`
			min-width: 34px;
			width: 34px;
			height: 34px;
			min-height: 34px;
			max-width: 34px;
			padding: 0;
			border-radius: 50%;
			overflow: hidden;
			white-space: nowrap;
			transform: none;
			border-color: rgba(255, 180, 236, 0.16);
			background:
				linear-gradient(
					135deg,
					rgba(255, 255, 255, 0.05),
					rgba(255, 255, 255, 0.018)
				),
				rgba(22, 12, 28, 0.82);

			svg {
				width: 16px;
				height: 16px;
				stroke-width: 2;
			}

			@media (max-width: ${THEME.mobileBreakpoint}) {
				min-width: 30px;
				width: 30px;
				height: 30px;
				min-height: 30px;
				max-width: 30px;

				svg {
					width: 14px;
					height: 14px;
				}
			}
		`}

	${(props) =>
		props.$back &&
		!props.$inHeader &&
		css`
			min-width: 42px;
			width: 42px;
			height: 42px;
			min-height: 42px;
			max-width: 42px;
			padding: 0;
			border-radius: 50%;
			overflow: hidden;
			white-space: nowrap;
			transform: translateY(-6px);
		`}

	${(props) =>
		props.$back &&
		props.$disabled &&
		css`
			cursor: default;
			opacity: 0.42;
		`}

	${(props) =>
		props.$back &&
		!props.$disabled &&
		css`
			cursor: pointer;
			opacity: 1;

			&:hover,
			&:focus-visible {
				border-color: rgba(255, 119, 244, 0.34);
				color: #ffffff;
				background:
					linear-gradient(
						135deg,
						rgba(255, 77, 242, 0.14),
						rgba(255, 180, 107, 0.13)
					),
					rgba(38, 20, 42, 0.88);
				box-shadow: 0 0 16px rgba(255, 77, 242, 0.12);
			}
		`}

	${(props) =>
		props.$muted &&
		css`
			min-width: 42px;
			width: 42px;
			padding: 0;
			cursor: default;
			opacity: 0.42;
			transform: translateY(-6px);
		`}

	${(props) =>
		props.$next &&
		css`
			border-color: rgba(255, 162, 241, 0.42);
			color: #ffffff;
			background:
				linear-gradient(
					135deg,
					rgba(203, 43, 255, 0.92),
					rgba(255, 58, 208, 0.7) 56%,
					rgba(255, 184, 119, 0.58)
				),
				rgba(42, 19, 48, 0.9);
			text-shadow: 0 0 10px rgba(255, 255, 255, 0.28);
			box-shadow:
				inset 0 1px 0 rgba(255, 255, 255, 0.16),
				0 0 18px rgba(255, 77, 242, 0.24);

			&:hover {
				background:
					linear-gradient(
						135deg,
						rgba(203, 43, 255, 0.98),
						rgba(255, 58, 208, 0.78) 56%,
						rgba(255, 184, 119, 0.64)
					),
					rgba(42, 19, 48, 0.92);
				box-shadow:
					inset 0 1px 0 rgba(255, 255, 255, 0.16),
					0 0 22px rgba(255, 77, 242, 0.3);
			}
		`}

	${(props) =>
		!props.$muted &&
		!props.$next &&
		css`
			&:hover {
				border-color: rgba(255, 119, 244, 0.34);
				color: #ffffff;
				background:
					linear-gradient(
						135deg,
						rgba(255, 77, 242, 0.14),
						rgba(255, 180, 107, 0.13)
					),
					rgba(38, 20, 42, 0.88);
				box-shadow: 0 0 16px rgba(255, 77, 242, 0.12);
				cursor: pointer;
				opacity: 1;
			}
		`}
`;

export const AgeStageSection = styled.section`
	min-height: 0;
	width: min(100%, 760px);
	margin: 0 auto;
	display: grid;
	justify-items: center;
	align-content: start;
	gap: 16px;
	padding: clamp(10px, 2.4vh, 24px) 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		gap: 12px;
		padding: 4px 0 12px;
	}
`;

export const AgeCard = styled.div`
	position: relative;
	width: min(100%, 620px);
	border: 1px solid rgba(255, 128, 238, 0.22);
	border-radius: 8px;
	padding: clamp(20px, 3.6vw, 34px) clamp(24px, 5vw, 44px);
	overflow: hidden;
	background:
		radial-gradient(
			circle at 52% 0%,
			rgba(255, 77, 242, 0.28),
			transparent 42%
		),
		linear-gradient(
			135deg,
			rgba(50, 17, 62, 0.86),
			rgba(12, 5, 18, 0.94) 58%,
			rgba(44, 19, 34, 0.88)
		);
	box-shadow:
		0 28px 90px rgba(0, 0, 0, 0.44),
		0 0 34px rgba(255, 77, 242, 0.12);
	isolation: isolate;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		border-radius: 16px;
		padding: 16px 18px 18px;
	}
`;

export const AgeOrbit = styled.div`
	position: absolute;
	inset: -38% 18% auto;
	height: 260px;
	border-radius: 50%;
	background: radial-gradient(
		circle,
		rgba(255, 211, 107, 0.34),
		rgba(255, 77, 242, 0.12) 40%,
		transparent 67%
	);
	filter: blur(4px);
	opacity: 0.72;
	z-index: -1;
`;

export const AgeReadout = styled.div`
	display: grid;
	justify-items: center;
	gap: 4px;
	margin-bottom: 20px;

	span {
		color: rgba(255, 226, 250, 0.74);
		font-size: 0.82rem;
		font-weight: 900;
		text-transform: uppercase;
	}

	strong {
		color: #fff;
		font-size: clamp(3.5rem, 8vw, 6rem);
		line-height: 0.88;
		font-weight: 900;
		text-shadow:
			0 0 20px rgba(255, 77, 242, 0.34),
			0 0 46px rgba(255, 211, 107, 0.14);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		margin-bottom: 14px;

		span {
			font-size: 0.74rem;
		}

		strong {
			font-size: clamp(2.5rem, 14vw, 3.2rem);
		}
	}
`;

export const AgeRangeInput = styled.input`
	width: 100%;
	height: 34px;
	appearance: none;
	-webkit-appearance: none;
	background: transparent;
	cursor: pointer;

	&::-webkit-slider-runnable-track {
		height: 8px;
		border-radius: 999px;
		border: 1px solid rgba(255, 255, 255, 0.14);
		background:
			linear-gradient(
				90deg,
				rgba(255, 77, 242, 0.96) 0 var(--age-progress, 19%),
				rgba(255, 255, 255, 0.1) var(--age-progress, 19%) 100%
			),
			rgba(255, 255, 255, 0.08);
		box-shadow: 0 0 18px rgba(255, 77, 242, 0.16);
	}

	&::-moz-range-track {
		height: 8px;
		border-radius: 999px;
		border: 1px solid rgba(255, 255, 255, 0.14);
		background: rgba(255, 255, 255, 0.1);
	}

	&::-moz-range-progress {
		height: 8px;
		border-radius: 999px;
		background: rgba(255, 77, 242, 0.96);
	}

	&::-webkit-slider-thumb {
		width: 28px;
		height: 28px;
		margin-top: -11px;
		appearance: none;
		-webkit-appearance: none;
		border: 2px solid rgba(255, 246, 255, 0.94);
		border-radius: 50%;
		background: linear-gradient(135deg, #ff4df2, #ffd36b);
		box-shadow:
			0 0 0 7px rgba(255, 77, 242, 0.14),
			0 0 24px rgba(255, 77, 242, 0.36);
	}

	&::-moz-range-thumb {
		width: 28px;
		height: 28px;
		border: 2px solid rgba(255, 246, 255, 0.94);
		border-radius: 50%;
		background: linear-gradient(135deg, #ff4df2, #ffd36b);
		box-shadow:
			0 0 0 7px rgba(255, 77, 242, 0.14),
			0 0 24px rgba(255, 77, 242, 0.36);
	}
`;

export const AgeScale = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 14px;
	margin-top: 12px;
	color: rgba(255, 231, 250, 0.62);
	font-size: 0.78rem;
	font-weight: 900;

	span:nth-child(2) {
		color: rgba(255, 211, 107, 0.86);
		text-transform: uppercase;
		min-width: 108px;
		text-align: center;
	}
`;

export const AgeConfirmButton = styled.button`
	min-width: 142px;
	min-height: 44px;
	border: 1px solid rgba(255, 180, 241, 0.38);
	border-radius: 999px;
	padding: 0 24px;
	color: #ffffff;
	background:
		radial-gradient(
			circle at 82% 12%,
			rgba(255, 211, 107, 0.24),
			transparent 34%
		),
		linear-gradient(135deg, rgba(190, 42, 255, 0.94), rgba(255, 77, 188, 0.82));
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.16),
		0 0 22px rgba(255, 77, 242, 0.2);
	font-weight: 900;
	cursor: pointer;
	transition:
		transform 160ms ease,
		box-shadow 160ms ease,
		border-color 160ms ease;

	&:hover,
	&:focus-visible {
		transform: translateY(-2px);
		border-color: rgba(255, 220, 250, 0.56);
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.2),
			0 0 28px rgba(255, 77, 242, 0.32);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		min-height: 44px;
	}
`;

export const FinalStageSection = styled.section`
	width: min(100%, 980px);
	margin: 0 auto;
	padding: clamp(14px, 3vh, 28px) 12px 18px 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		padding: 4px 0 12px;
	}
`;

export const FinalShell = styled.div`
	display: block;
`;

export const FinalNameCard = styled.label`
	position: relative;
	display: grid;
	gap: 6px;
	grid-column: span 2;
	padding: 14px 16px;
	border: 1px solid rgba(255, 214, 255, 0.16);
	border-radius: 22px;
	background:
		radial-gradient(
			circle at 88% 12%,
			rgba(255, 211, 107, 0.12),
			transparent 36%
		),
		linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.09),
			rgba(255, 255, 255, 0.028)
		),
		rgba(18, 7, 25, 0.68);
	box-shadow:
		0 22px 70px rgba(0, 0, 0, 0.28),
		inset 0 1px 0 rgba(255, 255, 255, 0.06);
	backdrop-filter: blur(24px) saturate(1.15);

	> span:first-child {
		color: rgba(255, 225, 250, 0.76);
		font-size: 0.78rem;
		font-weight: 900;
		text-transform: uppercase;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		grid-column: auto;
	}
`;

export const FinalNameRow = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: 10px;
	align-items: center;

	input {
		width: 100%;
		min-height: 42px;
		border: 1px solid rgba(255, 214, 255, 0.12);
		border-radius: 16px;
		padding: 0 15px;
		color: #ffffff;
		background: rgba(0, 0, 0, 0.26);
		outline: 0;
		font: inherit;
		font-weight: 800;

		&:focus {
			border-color: rgba(255, 119, 244, 0.42);
			box-shadow: 0 0 20px rgba(255, 77, 242, 0.12);
		}
	}
`;

export const FinalRandomButton = styled.button`
	width: 42px;
	height: 42px;
	border: 1px solid rgba(255, 214, 255, 0.18);
	border-radius: 16px;
	color: #fff;
	background:
		radial-gradient(
			circle at 78% 16%,
			rgba(255, 211, 107, 0.28),
			transparent 38%
		),
		linear-gradient(
			135deg,
			rgba(255, 77, 242, 0.2),
			rgba(255, 255, 255, 0.035)
		),
		rgba(28, 10, 38, 0.72);
	cursor: pointer;

	svg {
		width: 20px;
		height: 20px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
`;

export const FinalNameCount = styled.span`
	justify-self: end;
	color: rgba(255, 225, 250, 0.56);
	font-size: 0.78rem;
	font-weight: 800;
`;

export const FinalGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 14px;
	align-items: stretch;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		grid-template-columns: 1fr;
		gap: 10px;
	}
`;

const finalCardBase = css`
	position: relative;
	min-height: 82px;
	display: grid;
	align-content: center;
	justify-items: start;
	gap: 7px;
	border: 1px solid rgba(255, 214, 255, 0.14);
	border-radius: 20px;
	padding: 15px 48px 15px 16px;
	color: #ffffff;
	background:
		radial-gradient(
			circle at 92% 12%,
			rgba(255, 211, 107, 0.12),
			transparent 34%
		),
		linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.08),
			rgba(255, 255, 255, 0.022)
		),
		rgba(22, 9, 29, 0.68);
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.055),
		0 18px 42px rgba(0, 0, 0, 0.22);
	text-align: left;
	cursor: pointer;
	backdrop-filter: blur(22px) saturate(1.12);
	transition:
		transform 180ms ease,
		border-color 180ms ease,
		box-shadow 180ms ease,
		background 180ms ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 72px;
		padding: 12px 42px 12px 14px;
		border-radius: 16px;

		&:hover,
		&:focus-visible {
			transform: none;
		}
	}

	&:hover,
	&:focus-visible {
		transform: translateY(-2px);
		border-color: rgba(255, 119, 244, 0.38);
		background:
			radial-gradient(
				circle at 92% 12%,
				rgba(255, 211, 107, 0.18),
				transparent 34%
			),
			linear-gradient(
				135deg,
				rgba(255, 77, 242, 0.15),
				rgba(255, 255, 255, 0.036)
			),
			rgba(28, 10, 38, 0.76);
		box-shadow:
			0 20px 48px rgba(0, 0, 0, 0.27),
			0 0 22px rgba(255, 77, 242, 0.12);
	}

	span {
		color: rgba(255, 225, 250, 0.58);
		font-size: 0.72rem;
		font-weight: 700;
		text-transform: uppercase;
	}

	strong {
		font-size: 0.98rem;
		line-height: 1.1;
		font-weight: 700;
	}
`;

export const FinalCard = styled.button`
	${finalCardBase}
`;

export const FinalEditIcon = styled.i`
	position: absolute;
	right: 16px;
	top: 50%;
	width: 28px;
	height: 28px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(255, 255, 255, 0.24);
	border-radius: 50%;
	color: rgba(255, 255, 255, 0.84);
	font-style: normal;
	font-size: 0.84rem;
	transform: translateY(-50%);
`;

export const FinalInputCard = styled.label`
	${finalCardBase}
	cursor: text;

	input {
		width: 100%;
		min-width: 0;
		border: 0;
		border-bottom: 1px solid rgba(255, 214, 255, 0.16);
		outline: 0;
		padding: 2px 0 6px;
		color: #fff;
		background: transparent;
		font: inherit;
		font-size: 1rem;
		font-weight: 800;

		&:focus {
			border-color: rgba(255, 126, 237, 0.54);
			box-shadow: 0 8px 14px -12px rgba(255, 77, 242, 0.72);
		}
	}
`;

export const FinalCardVoice = styled(FinalCard)`
	grid-column: 3;
	align-self: stretch;
	background:
		radial-gradient(
			circle at 24% 50%,
			rgba(255, 77, 242, 0.22),
			transparent 34%
		),
		linear-gradient(
			100deg,
			rgba(26, 8, 34, 0.84),
			rgba(73, 20, 95, 0.56),
			rgba(255, 86, 203, 0.16)
		),
		rgba(12, 4, 18, 0.74);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		grid-column: auto;
	}
`;

export const FinalCardKinks = styled(FinalCard)`
	grid-column: span 2;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		grid-column: auto;
	}
`;

const confirmGlowPulse = keyframes`
	0%,
	100% {
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.18),
			0 0 24px rgba(255, 178, 108, 0.22),
			0 0 18px rgba(255, 77, 242, 0.18);
		border-color: rgba(255, 211, 107, 0.42);
	}

	50% {
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.28),
			0 0 36px rgba(255, 178, 108, 0.32),
			0 0 42px rgba(255, 77, 242, 0.38);
		border-color: rgba(255, 230, 160, 0.68);
	}
`;

const confirmSheenSweep = keyframes`
	0%,
	18% {
		transform: translateX(-130%) skewX(-18deg);
		opacity: 0;
	}

	28% {
		opacity: 0.85;
	}

	42%,
	100% {
		transform: translateX(140%) skewX(-18deg);
		opacity: 0;
	}
`;

export const FinalConfirmButton = styled(AgeConfirmButton)`
	position: relative;
	overflow: hidden;
	isolation: isolate;
	grid-column: 3;
	grid-row: 4;
	justify-self: stretch;
	align-self: stretch;
	width: 100%;
	min-height: 82px;
	height: 100%;
	margin-top: 0;
	border-radius: 20px;
	border-color: rgba(255, 211, 107, 0.42);
	color: #ffffff;
	background:
		radial-gradient(
			circle at 16% 22%,
			rgba(255, 255, 255, 0.22),
			transparent 38%
		),
		radial-gradient(
			circle at 88% 78%,
			rgba(255, 77, 242, 0.28),
			transparent 42%
		),
		linear-gradient(118deg, #ffb26c 0%, #ff4df2 46%, #b026ff 100%);
	font-size: clamp(1.12rem, 1.55vw, 1.28rem);
	font-weight: 900;
	letter-spacing: 0.04em;
	text-shadow: 0 1px 12px rgba(72, 8, 88, 0.42);
	animation: ${confirmGlowPulse} 3.2s ease-in-out infinite;

	&::before {
		content: '';
		position: absolute;
		inset: -1px;
		border-radius: inherit;
		padding: 1px;
		background: linear-gradient(
			135deg,
			rgba(255, 211, 107, 0.85),
			rgba(255, 77, 242, 0.9),
			rgba(176, 38, 255, 0.78)
		);
		mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		mask-composite: exclude;
		pointer-events: none;
		opacity: 0.88;
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			105deg,
			transparent 38%,
			rgba(255, 255, 255, 0.42) 50%,
			transparent 62%
		);
		transform: translateX(-130%) skewX(-18deg);
		animation: ${confirmSheenSweep} 4.8s ease-in-out infinite;
		pointer-events: none;
	}

	&:hover,
	&:focus-visible {
		animation-duration: 2.4s;
		background:
			radial-gradient(
				circle at 16% 22%,
				rgba(255, 255, 255, 0.28),
				transparent 38%
			),
			radial-gradient(
				circle at 88% 78%,
				rgba(255, 77, 242, 0.34),
				transparent 42%
			),
			linear-gradient(118deg, #ffc078 0%, #ff5ff5 46%, #c038ff 100%);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		grid-column: auto;
		grid-row: auto;
		justify-self: stretch;
		width: 100%;
		min-height: 44px;
		height: auto;
		border-radius: 999px;
		font-size: 1.06rem;
	}
`;

const pickerOverlayIn = keyframes`
	from {
		opacity: 0;
	}

	to {
		opacity: 1;
	}
`;

const pickerModalIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(18px) scale(0.96);
	}

	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
`;

const studioSpin = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

const studioOrbSpin = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

const studioOrbPulse = keyframes`
	0%,
	100% {
		transform: translate(-50%, -50%) scale(0.92);
		opacity: 0.82;
		box-shadow:
			0 0 18px rgba(255, 77, 242, 0.42),
			0 0 36px rgba(176, 38, 255, 0.24);
	}
	50% {
		transform: translate(-50%, -50%) scale(1.06);
		opacity: 1;
		box-shadow:
			0 0 28px rgba(255, 77, 242, 0.62),
			0 0 52px rgba(176, 38, 255, 0.34);
	}
`;

const studioFloatOrb = keyframes`
	0%,
	100% {
		transform: translate(0, 0) scale(1);
	}
	50% {
		transform: translate(12px, -10px) scale(1.04);
	}
`;

const studioShimmerSweep = keyframes`
	0% {
		transform: translateX(-120%) skewX(-18deg);
		opacity: 0;
	}
	35% {
		opacity: 0.7;
	}
	100% {
		transform: translateX(220%) skewX(-18deg);
		opacity: 0;
	}
`;

const studioFadeUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(14px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const studioCountdown = keyframes`
	0% {
		width: 100%;
	}
	100% {
		width: 0%;
	}
`;

export const GeneratingStageSection = styled.section`
	position: relative;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: clamp(12px, 3vh, 28px) 0;
	overflow: hidden;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 0;
		align-items: stretch;
	}

	&::before,
	&::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		filter: blur(72px);
		pointer-events: none;
		animation: ${studioFloatOrb} 8s ease-in-out infinite;
	}

	&::before {
		width: min(42vw, 320px);
		height: min(42vw, 320px);
		top: 8%;
		left: -8%;
		background: rgba(176, 38, 255, 0.22);
	}

	&::after {
		width: min(36vw, 280px);
		height: min(36vw, 280px);
		right: -6%;
		bottom: 6%;
		background: rgba(255, 77, 242, 0.14);
		animation-delay: -2.5s;
	}
`;

export const GeneratingCard = styled.div`
	position: relative;
	z-index: 1;
	width: min(100%, 560px);
	min-height: 320px;
	overflow: hidden;
	border: 1px solid rgba(246, 142, 255, 0.2);
	border-radius: 28px;
	padding: 0;
	text-align: center;
	background:
		linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, transparent 38%),
		rgba(14, 5, 20, 0.82);
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.06),
		0 28px 90px rgba(0, 0, 0, 0.48),
		0 0 48px rgba(255, 77, 242, 0.12);
	backdrop-filter: blur(24px) saturate(1.12);
	animation: ${studioFadeUp} 0.5s ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		flex: 1 1 auto;
		min-height: 0;
		max-height: none;
		display: flex;
		flex-direction: column;
		border-radius: 22px;
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.06),
			0 0 40px rgba(255, 77, 242, 0.1);
	}

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			115deg,
			transparent 0%,
			rgba(255, 255, 255, 0.14) 48%,
			transparent 76%
		);
		animation: ${studioShimmerSweep} 3.8s ease-in-out infinite;
		pointer-events: none;
	}
`;

export const GeneratingLoaderRoot = styled.div`
	position: relative;
	display: grid;
	place-items: center;
	align-content: center;
	gap: ${(props) => (props.$variant === 'overlay' ? '12px' : '18px')};
	width: 100%;
	min-height: ${(props) => (props.$variant === 'overlay' ? '100%' : '320px')};
	padding: ${(props) =>
		props.$variant === 'overlay' ? '28px 20px' : '34px 28px 42px'};

	${(props) =>
		props.$variant === 'overlay' &&
		css`
			min-height: 0;
			height: 100%;
		`}

	${(props) =>
		props.$variant === 'card' &&
		css`
			@media (max-width: ${THEME.mobileBreakpoint}) {
				flex: 1 1 auto;
				min-height: 0;
				width: 100%;
				display: flex;
				flex-direction: column;
				justify-content: center;
				gap: 14px;
				padding: 28px ${THEME.mobileGutter} 44px;
			}
		`}
`;

export const GeneratingLoaderOrbWrap = styled.div`
	display: grid;
	place-items: center;
	padding: ${(props) => (props.$variant === 'overlay' ? '4px 0' : '8px 0')};
`;

export const GeneratingLoaderOrb = styled.div`
	position: relative;
	width: ${(props) => (props.$compact ? '88px' : '112px')};
	height: ${(props) => (props.$compact ? '88px' : '112px')};

	&::before {
		content: '';
		position: absolute;
		inset: -10px;
		border-radius: 50%;
		background: conic-gradient(
			from 210deg,
			rgba(255, 77, 242, 0.95) 0deg,
			rgba(255, 211, 107, 0.85) ${(props) => props.$progress ?? 0}deg,
			rgba(255, 255, 255, 0.08) ${(props) => props.$progress ?? 0}deg,
			rgba(255, 255, 255, 0.08) 360deg
		);
		mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 0);
		-webkit-mask: radial-gradient(
			farthest-side,
			transparent calc(100% - 5px),
			#000 0
		);
		filter: drop-shadow(0 0 16px rgba(255, 77, 242, 0.28));
	}
`;

export const GeneratingLoaderOrbRing = styled.span`
	position: absolute;
	inset: ${(props) => `${((1 - (props.$scale ?? 1)) * 50)}%`};
	border-radius: 50%;
	border: 2px solid transparent;
	border-top-color: rgba(255, 77, 242, 0.92);
	border-right-color: rgba(255, 211, 107, 0.55);
	opacity: ${(props) => 0.35 + (props.$scale ?? 1) * 0.45};
	animation: ${studioOrbSpin} ${(props) => (props.$reverse ? '2.4s' : '1.7s')}
		linear infinite;
	animation-delay: ${(props) => props.$delay ?? 0}s;
	animation-direction: ${(props) => (props.$reverse ? 'reverse' : 'normal')};
`;

export const GeneratingLoaderOrbCore = styled.span`
	position: absolute;
	top: 50%;
	left: 50%;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: ${THEME.gradientPrimary};
	animation: ${studioOrbPulse} 2.1s ease-in-out infinite;
`;

export const GeneratingLoaderContent = styled.div`
	display: grid;
	gap: ${(props) => (props.$variant === 'overlay' ? '8px' : '12px')};
	justify-items: center;
	max-width: ${(props) => (props.$variant === 'overlay' ? '240px' : '380px')};
`;

export const GeneratingLoaderTitle = styled.h2`
	margin: 0;
	font-family: ${THEME.fontDisplay};
	color: #ffffff;
	font-size: ${(props) =>
		props.$compact
			? 'clamp(0.95rem, 2.8vw, 1.1rem)'
			: 'clamp(1.45rem, 3.4vw, 2.35rem)'};
	line-height: 1.05;
	letter-spacing: -0.02em;
	font-weight: 700;

	${(props) =>
		!props.$compact &&
		css`
			background: ${THEME.gradientHighlight};
			-webkit-background-clip: text;
			background-clip: text;
			-webkit-text-fill-color: transparent;
			filter: drop-shadow(0 0 12px rgba(255, 77, 242, 0.18));
		`}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: ${(props) =>
			props.$compact
				? 'clamp(0.95rem, 2.8vw, 1.1rem)'
				: 'clamp(1.12rem, 4.8vw, 1.38rem)'};
		line-height: 1.12;
	}
`;

export const GeneratingLoaderSubtitle = styled.p`
	margin: 0;
	color: rgba(255, 225, 250, 0.72);
	font-size: 0.92rem;
	font-weight: 600;
	line-height: 1.45;
	max-width: 28ch;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 0.82rem;
		line-height: 1.4;
		max-width: none;
	}
`;

export const GeneratingLoaderPhase = styled.span`
	color: rgba(255, 178, 236, 0.95);
	font-size: 0.78rem;
	font-weight: 800;
	letter-spacing: 0.12em;
	text-transform: uppercase;
`;

export const GeneratingLoaderSteps = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: 2px;
`;

export const GeneratingLoaderStep = styled.span`
	width: ${(props) => (props.$active ? '28px' : '8px')};
	height: 8px;
	border-radius: 999px;
	background: ${(props) => {
		if (props.$active) return THEME.gradientPrimary;
		if (props.$done) return 'rgba(255, 77, 242, 0.55)';
		return 'rgba(255, 255, 255, 0.14)';
	}};
	box-shadow: ${(props) =>
		props.$active ? '0 0 18px rgba(255, 77, 242, 0.42)' : 'none'};
	transition:
		width 320ms ease,
		background 320ms ease,
		box-shadow 320ms ease;
`;

export const GeneratingLoaderTime = styled.span`
	color: rgba(216, 189, 228, 0.9);
	font-size: 0.86rem;
	font-weight: 600;
`;

export const GeneratingLoaderProgressTrack = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: ${(props) => (props.$variant === 'overlay' ? '4px' : '6px')};
	background: rgba(255, 255, 255, 0.07);
	overflow: hidden;

	${(props) =>
		props.$variant === 'card' &&
		css`
			border-radius: 0 0 28px 28px;

			@media (max-width: ${THEME.mobileBreakpoint}) {
				border-radius: 0 0 22px 22px;
			}
		`}
`;

export const GeneratingLoaderProgressFill = styled.div`
	height: 100%;
	background: ${THEME.gradientPrimary};
	box-shadow: 0 0 16px rgba(255, 77, 242, 0.45);
	border-radius: 999px;
	transition: width 0.35s ease;
`;

export const GeneratingTimeEstimate = styled.span`
	color: rgba(216, 189, 228, 0.88);
	font-size: 0.9rem;
	font-weight: 600;
`;

export const GeneratingProgressTrack = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 8px;
	background: rgba(255, 255, 255, 0.08);
	border-radius: 0 0 28px 28px;
	overflow: hidden;
`;

export const GeneratingProgressBar = styled.div`
	height: 100%;
	width: 100%;
	background: ${THEME.gradientPrimary};
	transform-origin: left;
	animation: ${studioCountdown} ${({ $duration }) => $duration}s linear forwards;
`;

export const StudioSpinner = styled.div`
	width: 92px;
	height: 92px;
	border-radius: 50%;
	border: 3px solid rgba(255, 255, 255, 0.12);
	border-top-color: rgba(255, 77, 242, 0.96);
	border-right-color: rgba(255, 211, 107, 0.72);
	box-shadow: 0 0 34px rgba(255, 77, 242, 0.24);
	animation: ${studioSpin} 900ms linear infinite;
`;

export const ResultStageSection = styled.section`
	width: min(100%, 1080px);
	margin: 0 auto;
	padding: clamp(8px, 1.6vh, 16px) 12px 12px 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		padding: 4px 0 12px;
	}
`;

export const ResultShell = styled.div`
	display: grid;
	grid-template-columns: minmax(270px, 0.78fr) minmax(430px, 1.22fr);
	gap: clamp(20px, 3vw, 34px);
	align-items: center;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
		gap: 16px;
	}
`;

export const ResultPortrait = styled.figure`
	position: relative;
	width: min(100%, 370px);
	aspect-ratio: 0.72;
	justify-self: center;
	margin: 0;
	overflow: hidden;
	border: 1px solid rgba(255, 214, 255, 0.2);
	border-radius: 26px;
	background: #070509;
	isolation: isolate;
	box-shadow:
		0 28px 80px rgba(0, 0, 0, 0.44),
		0 0 30px rgba(255, 77, 242, 0.1);

	> div {
		position: absolute;
		inset: 0;
	}

	&::after {
		content: '';
		position: absolute;
		inset: 48% 0 0;
		z-index: 1;
		pointer-events: none;
		background: linear-gradient(
			180deg,
			transparent,
			rgba(5, 1, 8, 0.24) 38%,
			rgba(5, 1, 8, 0.92)
		);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: min(100%, 300px);
		max-height: min(52vh, 420px);
		border-radius: 20px;
	}
`;

export const ResultImage = styled.img`
	width: 100%;
	height: 100%;
	display: block;
	object-fit: cover;
	object-position: center top;
	transition:
		opacity 180ms ease,
		transform 180ms ease;

	&[data-loading='true'] {
		opacity: 0.28;
		transform: scale(1.02);
	}
`;

export const ResultLoadingOverlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 4;
	overflow: hidden;
	background:
		linear-gradient(180deg, rgba(10, 3, 15, 0.45), rgba(10, 3, 15, 0.82)),
		radial-gradient(circle at 50% 30%, rgba(176, 38, 255, 0.18), transparent 52%);
	backdrop-filter: blur(14px) saturate(1.1);

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			115deg,
			transparent 0%,
			rgba(255, 255, 255, 0.08) 48%,
			transparent 76%
		);
		animation: ${studioShimmerSweep} 4.2s ease-in-out infinite;
		pointer-events: none;
	}
`;

export const ResultLoadingEstimate = styled.span`
	color: rgba(216, 189, 228, 0.88);
	font-size: 0.82rem;
	font-weight: 600;
	text-transform: none;
	letter-spacing: 0;
`;

export const ResultLoadingSpinner = styled.div`
	width: 64px;
	height: 64px;
	border-radius: 50%;
	border: 3px solid rgba(255, 255, 255, 0.14);
	border-top-color: rgba(255, 77, 242, 0.96);
	border-right-color: rgba(255, 211, 107, 0.72);
	box-shadow: 0 0 28px rgba(255, 77, 242, 0.22);
	animation: ${studioSpin} 900ms linear infinite;
`;

export const ResultIdentity = styled.figcaption`
	position: absolute;
	left: 24px;
	right: 78px;
	bottom: 20px;
	z-index: 2;
	display: flex;
	align-items: baseline;
	gap: 7px;
	color: #fff;
	text-shadow: 0 2px 18px rgba(0, 0, 0, 0.72);

	strong {
		min-width: 0;
		overflow: hidden;
		font-size: clamp(1.35rem, 2.3vw, 1.9rem);
		font-weight: 760;
		line-height: 1;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	span {
		color: rgba(255, 239, 252, 0.72);
		font-size: clamp(1rem, 1.65vw, 1.28rem);
		font-weight: 620;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		left: 16px;
		right: 64px;
		bottom: 14px;

		strong {
			font-size: 1.15rem;
		}

		span {
			font-size: 0.92rem;
		}
	}
`;

export const ResultRerollButton = styled.button`
	position: absolute;
	top: 18px;
	right: 18px;
	z-index: 3;
	width: 46px;
	height: 46px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(255, 214, 255, 0.3);
	border-radius: 15px;
	padding: 0;
	color: #fff;
	background:
		linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.16),
			rgba(255, 255, 255, 0.04)
		),
		rgba(12, 4, 18, 0.48);
	box-shadow:
		0 14px 30px rgba(0, 0, 0, 0.3),
		0 0 20px rgba(255, 77, 242, 0.12);
	backdrop-filter: blur(18px) saturate(1.2);
	cursor: pointer;

	&:disabled {
		cursor: wait;
		opacity: 0.72;
	}

	svg {
		width: 22px;
		height: 22px;
		fill: none;
		stroke: currentColor;
		stroke-width: 1.7;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
`;

export const ResultDetails = styled.div`
	display: grid;
	gap: 14px;
`;

export const ResultGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(6, minmax(0, 1fr));
	gap: 9px;

	@media (max-width: 900px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 8px;
	}
`;

export const ResultAttribute = styled.article`
	grid-column: span 2;
	min-height: 56px;
	display: grid;
	align-content: start;
	gap: 4px;
	border: 1px solid rgba(255, 214, 255, 0.11);
	border-radius: 13px;
	padding: 9px 12px 8px;
	background:
		radial-gradient(
			circle at 90% 0%,
			rgba(255, 211, 107, 0.08),
			transparent 38%
		),
		linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.065),
			rgba(255, 255, 255, 0.016)
		),
		rgba(20, 8, 28, 0.5);
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.055),
		0 10px 26px rgba(0, 0, 0, 0.14);
	backdrop-filter: blur(18px) saturate(1.12);

	${(props) =>
		props.$wide &&
		css`
			grid-column: span 3;

			@media (max-width: 900px) {
				grid-column: span 2;
			}
		`}

	span {
		color: rgba(255, 225, 250, 0.58);
		font-size: 0.63rem;
		font-weight: 900;
		text-transform: uppercase;
	}

	strong {
		color: #ffffff;
		font-size: 0.84rem;
		line-height: 1.1;
	}
`;

export const ResultAwakenButton = styled.button`
	position: relative;
	overflow: hidden;
	min-height: 52px;
	border: 1px solid rgba(255, 185, 246, 0.3);
	border-radius: 16px;
	color: #ffffff;
	background:
		radial-gradient(
			circle at 84% 12%,
			rgba(255, 211, 107, 0.14),
			transparent 32%
		),
		linear-gradient(
			135deg,
			rgba(255, 255, 255, 0.13),
			rgba(255, 77, 242, 0.08)
		),
		rgba(35, 12, 43, 0.58);
	box-shadow:
		0 16px 38px rgba(0, 0, 0, 0.24),
		0 0 24px rgba(255, 77, 242, 0.1),
		inset 0 1px 0 rgba(255, 255, 255, 0.15);
	backdrop-filter: blur(20px) saturate(1.25);
	font: inherit;
	font-weight: 850;
	cursor: pointer;

	&:disabled {
		opacity: 0.6;
		cursor: wait;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 48px;
		border-radius: 14px;
		font-size: 0.92rem;
	}
`;

export const PickerOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 6000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: clamp(16px, 4vw, 28px);
	background:
		radial-gradient(
			circle at 50% 18%,
			rgba(176, 38, 255, 0.24),
			transparent 46%
		),
		radial-gradient(
			circle at 82% 82%,
			rgba(255, 77, 242, 0.12),
			transparent 40%
		),
		rgba(2, 1, 6, 0.84);
	backdrop-filter: blur(16px) saturate(1.25);
	animation: ${pickerOverlayIn} 240ms ease;
	overflow: hidden;
	overscroll-behavior: contain;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding:
			max(12px, env(safe-area-inset-top, 0px))
			${THEME.mobileGutter}
			max(12px, calc(${THEME.mobileNavHeight} + env(safe-area-inset-bottom, 0px)));
	}
`;

export const PickerModal = styled.div`
	position: relative;
	width: min(100%, 480px);
	max-height: min(78vh, 680px);
	display: grid;
	grid-template-rows: auto minmax(0, 1fr) auto;
	overflow: hidden;
	border-radius: 28px;
	border: 1px solid rgba(255, 180, 236, 0.24);
	background:
		radial-gradient(
			circle at 88% 0%,
			rgba(255, 211, 107, 0.16),
			transparent 34%
		),
		radial-gradient(
			circle at 10% 100%,
			rgba(255, 77, 242, 0.18),
			transparent 38%
		),
		linear-gradient(
			145deg,
			rgba(255, 255, 255, 0.09),
			rgba(255, 255, 255, 0.022)
		),
		rgba(14, 5, 20, 0.94);
	box-shadow:
		0 34px 96px rgba(0, 0, 0, 0.58),
		0 0 46px rgba(255, 77, 242, 0.16),
		inset 0 1px 0 rgba(255, 255, 255, 0.08);
	backdrop-filter: blur(28px) saturate(1.18);
	animation: ${pickerModalIn} 340ms cubic-bezier(0.22, 1, 0.36, 1);

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			145deg,
			rgba(255, 255, 255, 0.07) 0%,
			transparent 42%,
			rgba(255, 77, 242, 0.05) 100%
		);
		pointer-events: none;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		max-height: min(
			85dvh,
			calc(
				100dvh - ${THEME.mobileNavHeight} - env(safe-area-inset-top, 0px) -
					env(safe-area-inset-bottom, 0px) - 24px
			)
		);
		min-height: unset;
		border: 1px solid rgba(255, 180, 236, 0.24);
		border-radius: 22px;
		box-shadow:
			0 34px 96px rgba(0, 0, 0, 0.58),
			0 0 46px rgba(255, 77, 242, 0.16);
	}
`;

export const PickerHeader = styled.div`
	position: relative;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 20px 20px 14px;
	border-bottom: 1px solid rgba(255, 214, 255, 0.12);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding:
			max(16px, calc(12px + env(safe-area-inset-top, 0px)))
			${THEME.mobileGutter}
			12px;
	}
`;

export const PickerTitle = styled.h3`
	margin: 0;
	font-size: clamp(1.05rem, 2.4vw, 1.22rem);
	font-weight: 800;
	line-height: 1.15;
	color: #ffffff;
	-webkit-text-fill-color: #ffffff;
`;

export const PickerCloseButton = styled.button`
	flex: 0 0 auto;
	width: 38px;
	height: 38px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(255, 214, 255, 0.22);
	border-radius: 50%;
	color: rgba(255, 245, 255, 0.92);
	background:
		radial-gradient(
			circle at 70% 20%,
			rgba(255, 77, 242, 0.18),
			transparent 42%
		),
		rgba(255, 255, 255, 0.04);
	cursor: pointer;
	transition:
		transform 160ms ease,
		border-color 160ms ease,
		box-shadow 160ms ease;

	&:hover,
	&:focus-visible {
		transform: translateY(-1px);
		border-color: rgba(255, 126, 237, 0.52);
		box-shadow: 0 0 18px rgba(255, 77, 242, 0.2);
	}

	svg {
		width: 16px;
		height: 16px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
	}
`;

export const PickerOptions = styled.div`
	position: relative;
	z-index: 1;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
	gap: 10px;
	padding: 16px 20px;
	overflow-y: auto;
	overscroll-behavior: contain;
	-webkit-overflow-scrolling: touch;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		grid-template-columns: 1fr;
		padding: 12px ${THEME.mobileGutter} 16px;
	}
`;

export const PickerOption = styled.button`
	position: relative;
	min-height: 50px;
	padding: 12px 14px;
	border-radius: 16px;
	border: 1px solid
		${(props) =>
			props.$active
				? 'rgba(255, 126, 237, 0.62)'
				: 'rgba(255, 214, 255, 0.14)'};
	color: #ffffff;
	-webkit-text-fill-color: #ffffff;
	background: ${(props) =>
		props.$active
			? 'radial-gradient(circle at 82% 12%, rgba(255, 211, 107, 0.22), transparent 38%), linear-gradient(135deg, rgba(255, 77, 242, 0.28), rgba(176, 38, 255, 0.16))'
			: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.015)), rgba(18, 7, 25, 0.62)'};
	box-shadow: ${(props) =>
		props.$active
			? '0 0 22px rgba(255, 77, 242, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.12)'
			: 'inset 0 1px 0 rgba(255, 255, 255, 0.04)'};
	font-weight: 700;
	font-size: 0.92rem;
	line-height: 1.2;
	text-align: left;
	cursor: pointer;
	overflow: hidden;
	transition:
		transform 160ms ease,
		border-color 160ms ease,
		box-shadow 160ms ease,
		background 160ms ease;

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			120deg,
			transparent 40%,
			rgba(255, 255, 255, 0.16) 50%,
			transparent 60%
		);
		transform: translateX(-120%);
		transition: transform 420ms ease;
		pointer-events: none;
	}

	&:hover,
	&:focus-visible {
		transform: translateY(-2px);
		border-color: rgba(255, 126, 237, 0.48);
		box-shadow:
			0 10px 24px rgba(0, 0, 0, 0.24),
			0 0 18px rgba(255, 77, 242, 0.12);

		&::after {
			transform: translateX(120%);
		}
	}

	&[aria-pressed='true'] {
		border-color: rgba(255, 126, 237, 0.72);
	}
`;

export const PickerDoneButton = styled.button`
	position: relative;
	z-index: 1;
	margin: 4px 20px 20px;
	min-height: 52px;
	border: 1px solid rgba(255, 180, 241, 0.38);
	border-radius: 18px;
	padding: 0 20px;
	color: #ffffff;
	background:
		radial-gradient(
			circle at 82% 12%,
			rgba(255, 211, 107, 0.24),
			transparent 34%
		),
		linear-gradient(135deg, rgba(190, 42, 255, 0.94), rgba(255, 77, 188, 0.82));
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.16),
		0 0 22px rgba(255, 77, 242, 0.2);
	font-weight: 900;
	font-size: 0.95rem;
	cursor: pointer;
	overflow: hidden;
	transition:
		transform 160ms ease,
		box-shadow 160ms ease,
		border-color 160ms ease;

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			105deg,
			transparent 38%,
			rgba(255, 255, 255, 0.35) 50%,
			transparent 62%
		);
		transform: translateX(-130%) skewX(-18deg);
		animation: ${confirmSheenSweep} 5.2s ease-in-out infinite;
		pointer-events: none;
	}

	&:hover,
	&:focus-visible {
		transform: translateY(-2px);
		border-color: rgba(255, 220, 250, 0.56);
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.2),
			0 0 28px rgba(255, 77, 242, 0.32);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		margin: 4px ${THEME.mobileGutter}
			calc(16px + env(safe-area-inset-bottom, 0px));
		min-height: 48px;
		border-radius: 14px;
		font-size: 0.88rem;
	}
`;
